import Container from "../../../components/element/container";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MDBModalBody } from "mdb-react-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/element/button";
import Col from "../../../components/element/col";
import Icon from "../../../components/element/icon";
import Image from "../../../components/element/image";
import Row from "../../../components/element/row";
import FullModal from "../../../components/modal/full.modal";
import AgreementReaderModal from "../../../components/modal/agreement.reader.modal";
import { showToast } from "../../../helpers/show.toast";
import {
  clearPaymentMethods,
  getPaymentMethods,
  payOrder,
} from "../../../redux/reducer/order.reducer";

function toPaymentGroups(paymentMethods) {
  const source =
    paymentMethods?.channels ||
    paymentMethods?.payment_methods ||
    paymentMethods?.methods ||
    paymentMethods;

  if (Array.isArray(source)) {
    return source.length > 0 ? [["Payment Methods", source]] : [];
  }

  if (!source || typeof source !== "object") return [];

  return Object.entries(source).filter(
    ([, methods]) => Array.isArray(methods) && methods.length > 0,
  );
}

const toggleGroupKey = (current, group) => (current === group ? null : group);

function groupIcon(group) {
  const name = String(group || "").toLowerCase();

  if (name.includes("wallet")) return "solar:wallet-linear";
  if (name.includes("card")) return "solar:card-linear";
  if (name.includes("bank") || name.includes("fpx")) {
    return "solar:buildings-2-linear";
  }

  return "solar:card-2-linear";
}

export default function PaymentMethodModal({
  open,
  order,
  onClose,
  onSuccess,
  // Set when the service agreement was already accepted upstream, so the
  // buyer is not asked to read and agree a second time.
  serviceAgreementAccepted = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loadedOrderId = useRef(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(
    serviceAgreementAccepted,
  );
  const [showAgreement, setShowAgreement] = useState(false);
  const { payOrderLoading, paymentMethods, paymentMethodsLoading } =
    useSelector((state) => state.order);
  const paymentGroups = useMemo(
    () => toPaymentGroups(paymentMethods),
    [paymentMethods],
  );

  const loadPaymentMethods = () => {
    setSelectedPayment(null);
    setOpenGroup(null);
    setAgreementAccepted(serviceAgreementAccepted);
    setShowAgreement(false);
    dispatch(clearPaymentMethods());

    dispatch(getPaymentMethods())
      .unwrap()
      .catch((error) => {
        showToast(
          "Unable to load payment methods",
          error?.response?.data?.message || "Please try again shortly.",
          "error",
        );
      });
  };

  const payableId = order?.orderId || order?.consignId || null;

  useEffect(() => {
    if (!open) {
      loadedOrderId.current = null;
      return;
    }
    const key = payableId || "missing-order";
    if (loadedOrderId.current === key) return;

    loadedOrderId.current = key;
    loadPaymentMethods();
    // Payment methods are reloaded when a different order is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payableId]);

  const close = () => {
    loadedOrderId.current = null;
    setSelectedPayment(null);
    setOpenGroup(null);
    setAgreementAccepted(serviceAgreementAccepted);
    setShowAgreement(false);
    onClose?.();
  };

  const handlePayOrder = () => {
    if (payOrderLoading) return;
    if (!payableId) {
      showToast("Payment unavailable", "No payable order was found. Please refresh the page or contact support.", "error");
      return;
    }
    if (!selectedPayment || (requiresServiceAgreement && !agreementAccepted)) return;

    if (
      selectedPayment.tenant_channel_id === undefined ||
      selectedPayment.tenant_channel_id === null
    ) {
      showToast(
        "Payment method unavailable",
        "Please select another payment method.",
        "error",
      );
      return;
    }

    const payload = {
      order_id: payableId,
      tenant_channel_id: selectedPayment.tenant_channel_id,
    };

    if (
      selectedPayment.provider_channel_id !== undefined &&
      selectedPayment.provider_channel_id !== null
    ) {
      payload.provider_channel_id = selectedPayment.provider_channel_id;
    }

    dispatch(payOrder(payload))
      .unwrap()
      .then((response) => {
        const paymentUrl = response?.data?.payment_data?.result?.url || null;

        showToast(
          "Payment created",
          paymentUrl
            ? "Redirecting you to the secure payment page."
            : "Your payment request has been submitted.",
          "success",
        );
        close();
        onSuccess?.();

        if (paymentUrl) window.location.assign(paymentUrl);
      })
      .catch((error) => {
        showToast(
          "Unable to create payment",
          error?.response?.data?.message ||
            "Please verify your payment method and try again.",
          "error",
        );
      });
  };

  const requiresServiceAgreement =
    (order?.kind === "inspection" || Boolean(order?.consignId)) && !serviceAgreementAccepted;

  const handleAgreementChange = (event) => {
    if (agreementAccepted) {
      setAgreementAccepted(false);
      return;
    }

    event.preventDefault();
    setShowAgreement(true);
  };

  return (
    <>
      <FullModal
        independent
        show={open}
        close={close}
        showCloseButton
        centered
        screenSize="lg"
        className="product-action-modal reservation-modal payment-method-modal overflow-hidden"
        dialogClassName="action-modal-dialog reservation-dialog"
        contentClassName="action-modal-content reservation-content"
        content={
          <MDBModalBody className="action-modal-body reservation-body">
            <Col className="_col reservation-payment-view">
              <Row className="_row reservation-payment-header">
                <Col className="_col payment-heading-copy">
                  <h2 className="_label payment-title">
                    {t("Choose Payment Method")}
                  </h2>
                  <p className="_label payment-subtitle">
                    {t("Pay order")}{" "}
                    {order?.reservationId || order?.orderId || "-"}
                  </p>
                </Col>
              </Row>

              <Row
                className="_row reservation-payment-summary"
                style={
                  order?.image
                    ? {
                        backgroundImage: `linear-gradient(90deg, #101b28 0%, rgba(9, 18, 28, 0.96) 58%, rgba(9, 18, 28, 0.62) 100%), url(${order.image})`,
                      }
                    : undefined
                }
              >
                <Row className="_row payment-summary-copy">
                  <Icon
                    icon="solar:shield-check-linear"
                    className="_svg payment-secure"
                  />
                  <Col className="_col payment-summary-text">
                    <span className="_label payment-summary-label">
                      {t("Amount Due")}
                    </span>
                    <strong className="_label payment-summary-value">
                      {order?.paymentAmount || "RM 5,000"}
                    </strong>
                  </Col>
                </Row>
                <span className="_label payment-refundable">
                  {t("Secure Payment")}
                </span>
              </Row>

              {paymentMethodsLoading ? (
                <Container tag="div" className="payment-method-loading">
                  {Array.from({ length: 4 }, (_, index) => (
                    <span className="payment-method-skeleton" key={index} />
                  ))}
                </Container>
              ) : paymentGroups.length > 0 ? (
                <Col className="_col payment-method-groups">
                  {paymentGroups.map(([group, methods]) => {
                    const expanded = openGroup === group;

                    return (
                      <Col
                        className={`_col payment-method-group ${
                          expanded ? "open" : "collapsed"
                        }`}
                        key={group}
                      >
                        <button
                          type="button"
                          className="payment-group-header"
                          aria-expanded={expanded}
                          onClick={() =>
                            setOpenGroup((current) =>
                              toggleGroupKey(current, group),
                            )
                          }
                        >
                          <span className="payment-group-icon">
                            <Icon icon={groupIcon(group)} />
                          </span>
                          <h3 className="_label payment-group-title">
                            {group}
                          </h3>
                          <Icon
                            icon={
                              expanded
                                ? "mingcute:up-line"
                                : "mingcute:down-line"
                            }
                            className="_svg payment-group-chevron"
                          />
                        </button>
                        {expanded && (
                          <Container tag="div" className="payment-method-list">
                            {methods.map((method, index) => {
                              const methodKey = `${group}-${
                                method.tenant_channel_id ?? "channel"
                              }-${
                                method.provider_channel_id ??
                                method.name ??
                                method.display_name ??
                                index
                              }`;
                              const active = selectedPayment === method;
                              const methodName =
                                method.display_name ||
                                method.name ||
                                "Payment method";

                              return (
                                <Button
                                  className={`_button payment-method-item ${
                                    active ? "active" : ""
                                  }`}
                                  key={methodKey}
                                  onClick={() => setSelectedPayment(method)}
                                >
                                  {method.image_url ? (
                                    <Image
                                      src={method.image_url}
                                      onError={(event) => {
                                        event.currentTarget.parentElement.style.display =
                                          "none";
                                      }}
                                      alt={methodName}
                                      className="_img payment-method-logo"
                                    />
                                  ) : (
                                    <span className="payment-method-logo-fallback">
                                      <Icon icon="solar:card-2-linear" />
                                    </span>
                                  )}
                                  <Col className="_col payment-method-copy">
                                    <span className="_label payment-method-name">
                                      {methodName}
                                    </span>
                                    <span className="_label payment-method-note">
                                      {t("Secure online payment")}
                                    </span>
                                  </Col>
                                  <span className="payment-method-radio">
                                    {active && (
                                      <Icon
                                        icon="material-symbols:check-rounded"
                                        className="_svg payment-selected"
                                      />
                                    )}
                                  </span>
                                </Button>
                              );
                            })}
                          </Container>
                        )}
                      </Col>
                    );
                  })}
                </Col>
              ) : (
                <Col className="_col payment-method-empty">
                  <Icon
                    icon="solar:card-search-linear"
                    className="_svg payment-empty"
                  />
                  <p className="_label payment-empty-title">
                    {t("No payment methods available")}
                  </p>
                  <Button
                    className="_button payment-retry"
                    onClick={loadPaymentMethods}
                  >
                    <span className="_label payment-retry">{t("Try Again")}</span>
                  </Button>
                </Col>
              )}

              <Row className="_row payment-security-note security-note-inline">
                <Icon tag="span"
                  icon="solar:lock-keyhole-linear" className="security-lock _svg payment-lock"
                />
                <span className="_label payment-security-copy">
                  {t("Your payment is encrypted and securely processed.")}
                </span>
              </Row>

              {requiresServiceAgreement && (
                <Container tag="div" className="reservation-agreement payment-agreement">
                  <article className="checkbox-wrapper agreement-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={agreementAccepted}
                        onChange={handleAgreementChange}
                      />
                      <span className="checkbox" />
                    </label>
                  </article>
                  <button type="button" onClick={() => setShowAgreement(true)}>
                    {t("I have read and agree to the")} <u>{t("Service Agreement")}</u>
                  </button>
                </Container>
              )}

              <Button
                className="_button action-modal-primary payment-proceed"
                disabled={
                  !payableId ||
                  !selectedPayment ||
                  paymentMethodsLoading ||
                  payOrderLoading ||
                  (requiresServiceAgreement && !agreementAccepted)
                }
                onClick={handlePayOrder}
              >
                <span className="_label payment-proceed-label">
                  {payOrderLoading ? "Processing..." : "Proceed to Payment"}
                </span>
                {/* {!payOrderLoading && (
                  <Icon
                    icon="solar:arrow-right-linear"
                    className="_svg payment-proceed-icon"
                  />
                )} */}
              </Button>
              {!payableId && (
                <p role="alert" className="payment-order-error">
                  {t("No payable order was found. Please refresh the page or contact support to arrange your inspection payment.")}
                </p>
              )}
            </Col>
          </MDBModalBody>
        }
      />
      {requiresServiceAgreement && (
        <AgreementReaderModal
          show={showAgreement}
          type="service"
          record={order?.record || order}
          accepted={agreementAccepted}
          onAcceptedChange={setAgreementAccepted}
          onClose={() => setShowAgreement(false)}
        />
      )}
    </>
  );
}
